import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  input,
  linkedSignal,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { MatMenu, MatMenuTrigger } from "@angular/material/menu";
import { FormArray, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { startWith, tap } from "rxjs/operators";
import { combineLatest, debounceTime } from "rxjs";
import { mergeWith } from "lodash";
import {
  BmExpGroupFg,
  BmItemForm,
  BmMgmFeePcntForm,
  BudgetModelFormGroup,
} from "../../../pojo/bm-form";
import { SwitcherDashBracesPipe } from "../../../../../../util/pipes/switcherDashBraces.pipe";
import { ButtonComponent } from "../../../../../shared/component/buttons/button.component";
import { TableGroupIconComponent } from "../../../../../shared/component/table-group-icon/table-group-icon.component";
import { OnHoverDirective } from "../../../../../../util/directives/on-hover-directive";
import { InputComponent } from "../../../../../shared/component/inputs/input-component/input.component";
import { GroupActionHelperService } from "../../service/group-action-helper.service";
import { BmDefaultFormCreatorService } from "../../../services/general/bm-default-form-creator.service";
import { BmSubscriptionsService } from "../../../services/general/subscription/bm-subscriptions.service";
import { BmCalcHelperService } from "../../../services/general/bm-calc-helper.service";
import { BmExpGroup, BmPeriodEnum } from "../../../pojo/bm-classes";
import { BmCalcType, BmGroupAction, BmGroupType, BmDataFillingType } from "../../../pojo/bm-enums";
import { DropDownItemComponent } from "../../../../../shared/component/drop-down-item/drop-down-item.component";
import { ThreeDotsMenuComponent } from "../../../../../shared/component/three-dots-menu/three-dots-menu.component";
import { NgClass } from "@angular/common";
import { CdkDrag, CdkDragHandle } from "@angular/cdk/drag-drop";
import { AllIconsComponent } from "../../../../../shared/component/all-icons/all-icons.component";
import { SelectorComponent } from "../../../../../shared/component/inputs/selector/selector.component";
import { BmExpensesCalcService } from "../../../services/expenses/bm-expenses-calc.service";
@Component({
  selector: '[app-table-budget-model-body-operating-expenses]',
  imports: [
    ReactiveFormsModule,
    TableGroupIconComponent,
    ButtonComponent,
    SwitcherDashBracesPipe,
    NgClass,
    OnHoverDirective,
    CdkDrag,
    AllIconsComponent,
    ThreeDotsMenuComponent,
    MatMenuTrigger,
    MatMenu,
    DropDownItemComponent,
    InputComponent,
    SelectorComponent,
    CdkDragHandle,
  ],
  templateUrl: './table-budget-model-body-operating-expenses.component.html',
  styleUrl: '../../table-budget-model.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableBudgetModelBodyOperatingExpensesComponent implements OnChanges {
  readonly BmGroupType = BmGroupType;
  readonly BmGroupAction = BmGroupAction;
  readonly BmPeriodEnum = BmPeriodEnum;
  readonly BmDataFillingType = BmDataFillingType;
  readonly dataFillingTypes = Object.values(BmDataFillingType);

  budgetForm = input.required<BudgetModelFormGroup>();
  collapsedYears = input.required<Map<number, boolean>>();
  isEditState = input.required<boolean>();

  budgetFormValue$$ = linkedSignal(() => {
    return this.budgetForm().getRawValue();
  });

  destroyRef = inject(DestroyRef);
  cdr = inject(ChangeDetectorRef);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.budgetForm) {
      this.subscribeOnFormChanges();
    }
  }

  subscribeOnFormChanges(): void {
    combineLatest([
      this.budgetForm().controls.modelData.controls.operatingExpenses.valueChanges.pipe(
        startWith(null),
        takeUntilDestroyed(this.destroyRef)),
      this.budgetForm().controls.yearsNumber.valueChanges.pipe(
        startWith(-1),
        takeUntilDestroyed(this.destroyRef)),
    ]).pipe(
      debounceTime(5), // form updates multiple times. We want to asap but leave an interval for batch updates.
      tap(([_, yearsNumber]) => {
        const isCopyEnabled = yearsNumber === -1 || yearsNumber === this.budgetFormValue$$().yearsNumber;
        this.updateLocalFormValue(isCopyEnabled);
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }

  /**
   * OPERATIONAL EXPENSES
   *
   * Handle actions ADD, DELETE, DUPLICATE, RENAME in separate service
   * @param action type of action
   * @param groupFG selected FormGroup
   */
  onOpExpGroupAction(action: BmGroupAction, groupFG?: FormGroup<BmExpGroupFg>): void {
    const groupsFA = this.budgetForm().controls.modelData.controls.operatingExpenses.controls.groups;

    GroupActionHelperService.handleGroupAction(
      action,
      groupsFA,
      groupFG,
      (name: string, priority: number): BmExpGroup =>
        BmDefaultFormCreatorService.createExpensesGroup(name, 0, BmDataFillingType.MANUAL, this.budgetForm().controls.yearsNumber.value, priority),
      (model: BmExpGroup): FormGroup<BmExpGroupFg> =>
        BmExpensesCalcService.createExpGroupForm(model),
      (fg) => BmSubscriptionsService.subscribeToOpExpGroup(fg),
      'Operating expenses group',
    );
    BmExpensesCalcService.recalculateSectionTotal(this.budgetForm().controls.modelData.controls.operatingExpenses);
    this.updateLocalFormValue(false);
  }

  /**
   * Handle edit item action (clicking on edit.svg in table sell)
   *
   * (EDIT)   Editable value was DISABLED => Make ENABLED and with calcType MANUAL
   * (CANCEL) Editable value was ENABLE => Make DISABLE and with calcType GENERATED
   *
   * @param item selected control
   * @param groupType needed to specify the group with unique logic
   */
  editItemValue(item: FormGroup<BmItemForm>, groupType?: BmGroupType): void {
    const itemsFa = item.parent as FormArray<FormGroup<BmItemForm>>;

    if (item.controls.itemValue.disabled) {
      item.controls.itemValue.enable({ emitEvent: false });
      item.controls.calcType.patchValue(BmCalcType.MANUAL, { emitEvent: false });
    } else {
      item.controls.itemValue.disable({ emitEvent: false });
      let previousValue: number;
      const formGroup = itemsFa.parent as FormGroup<any>; // could be various FormGroups
      const standardValue = formGroup.value.standardValue as number;

      switch (groupType) {
        case BmGroupType.MGM_FEE_PCNT:
          const formGroupMgmFee = itemsFa.parent as FormGroup<BmMgmFeePcntForm>;
          const standardValueMgmFee = formGroupMgmFee.value.standardValue;

          if (item.getRawValue().period.type === BmPeriodEnum.YEAR) {
            previousValue = standardValueMgmFee;
          }
          if (item.getRawValue().period.type === BmPeriodEnum.QUARTER) {
            previousValue = standardValueMgmFee / 4;
          }
          break;
        case BmGroupType.EXPENSES:
          previousValue = this.handleExpensesItemChange(itemsFa, item);
          break;
        case BmGroupType.INVESTMENT_VALUE:
          previousValue = 0;
          break;
        case BmGroupType.INVESTMENT_PCNT:
          previousValue = 0;
          break;
        default:
          previousValue = standardValue;
          break;
      }
      item.controls.calcType.patchValue(BmCalcType.GENERATED, { emitEvent: false });
      item.controls.itemValue.markAsDirty(); // mark as dirty = mark as changed by user in UI => needed to update values in service
      item.controls.itemValue.patchValue(previousValue);
    }
  }

  /**
   * Helper method to handle previous value calculation on 'x' icon click in Expenses group
   * Based on dataFillingType and standardValueExpenses will be previous value selected
   * @param itemsFa array of group Items
   * @param item selected Item
   */
  private handleExpensesItemChange(itemsFa: FormArray<FormGroup<BmItemForm>>, item: FormGroup<BmItemForm>): number {
    const formGroupExpenses = itemsFa.parent as FormGroup<BmExpGroupFg>;
    const standardValueExpenses = formGroupExpenses.value.standardValue;
    const isYearItem = item.getRawValue().period.type === BmPeriodEnum.YEAR;
    let previousValue: number;

    switch (formGroupExpenses.value.dataFillingType) {
      case BmDataFillingType.YEARLY:
        previousValue = isYearItem ? standardValueExpenses : (standardValueExpenses / 4);
        break;
      case BmDataFillingType.QUARTERLY:
        previousValue = isYearItem ? (standardValueExpenses * 4) : standardValueExpenses;
        break;
      case BmDataFillingType.MONTHLY:
        previousValue = isYearItem ? (standardValueExpenses * 12) : (standardValueExpenses * 3);
        break;
      case BmDataFillingType.MANUAL:
        if (isYearItem) {
          const quartersFG = BmCalcHelperService.findQuarterItemsInSelectedYear(formGroupExpenses.controls.items, item.value.period.yearNumber);
          previousValue = BmCalcHelperService.calculateSumOfQuarterValues(quartersFG);
        } else {
          previousValue = 0;
        }
        break;
    }
    return previousValue;
  }

  private updateLocalFormValue(isCopyEnabled: boolean): void {
    const newRawValue = this.budgetForm().getRawValue();
    if (isCopyEnabled) {
      mergeWith(this.budgetFormValue$$(), newRawValue);
    } else {
      this.budgetFormValue$$.set(newRawValue);
    }
    this.cdr.detectChanges();
  }
}
